import { Navigate } from 'react-router-dom';
import { useProfile } from '../../hooks/useProfile';
import { useOrganization } from '../../hooks/useOrganization';
import { RoleProtectedSkeleton } from './RoleProtectedSkeleton';
import { uiLogger } from '../../utils/loggerInstance';
import { Button } from '../ui/button';
import { AccountLoadErrorCard } from './AccountLoadErrorCard';
import { PROFILE_ENTERPRISE_BILLING_PATH } from '../../services/billingEntitlementPolicy';

interface PersonalBillingProtectedRouteProps {
  children: React.ReactNode;
  redirectPath?: string;
}

/**
 * Route protection for personal billing pages (billing, pricing).
 *
 * Rules:
 * - Users NOT in an organization: ✅ Allow access (personal billing)
 * - Users in organization as admin/owner: ❌ Redirect to enterprise-billing (should use org billing)
 * - Users in organization as member: ❌ Redirect to profile (no billing access)
 *
 * @param children - Child components to render if authorized
 * @param redirectPath - Path to redirect to if user is in organization (default: '/profile')
 */
export const PersonalBillingProtectedRoute: React.FC<PersonalBillingProtectedRouteProps> = ({
  children,
  redirectPath = '/profile',
}) => {
  const {
    data: userProfile,
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile,
  } = useProfile();
  const {
    data: organization,
    isLoading: orgLoading,
    isError: orgError,
    refetch: refetchOrganization,
  } = useOrganization();

  const loading = profileLoading || orgLoading;

  // Show skeleton while loading
  if (loading) {
    return <RoleProtectedSkeleton />;
  }

  // Profile query failed – show error so user isn't stuck on skeleton
  if (profileError) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[40vh] p-4'>
        <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>
            Couldn&apos;t load your profile
          </h3>
          <p className='text-sm text-gray-500 mb-6'>
            Something went wrong loading your account. Please try again.
          </p>
          <Button onClick={() => refetchProfile()} variant='default'>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (orgError) {
    return (
      <AccountLoadErrorCard
        onRetry={async () => {
          await refetchOrganization();
        }}
      />
    );
  }

  if (!userProfile) {
    return <RoleProtectedSkeleton />;
  }

  const organizationRole = organization?.role ?? null;
  const belongsToOrganization = !!organization;

  // If user is in an organization, they should not access personal billing pages
  if (belongsToOrganization) {
    const isAdminOrOwner = organizationRole === 'admin' || organizationRole === 'owner';

    if (isAdminOrOwner) {
      // Admin/Owner should use enterprise billing instead
      uiLogger.warn(
        'Admin/Owner attempted to access personal billing - redirecting to enterprise billing',
        {
          userRole: organizationRole,
          attemptedPath: window.location.pathname,
        },
      );
      return <Navigate to={PROFILE_ENTERPRISE_BILLING_PATH} replace />;
    } else {
      // Member has no billing access
      uiLogger.warn('Organization member attempted to access billing - access denied', {
        userRole: organizationRole,
        attemptedPath: window.location.pathname,
      });
      return <Navigate to={redirectPath} replace />;
    }
  }

  // User is not in an organization - allow access to personal billing
  return <>{children}</>;
};
